import { useState } from 'react'
import { Check } from 'lucide-react'
import { toast } from 'sonner'
import { Label } from '@/components/ui/label'
import { InlineFieldInput } from '@/components/inline-field-group'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { LoadingButton } from '@/components/loading-button'
import { ExpandableSettingCell } from '@/components/setting-cell'
import {
  useCorrectProductFacts,
  useProductFactHistories,
  useProductFacts,
  useProductShippingFacts,
} from '@/api/hooks'
import {
  SourceEmailLink,
  type DataSourceEmail,
} from '@/features/manufacturer-data/source-email-link'
import { formatNumber, parseUTCDate } from '@/lib/utils'
import type { components } from '@/api/schema'

type Fact = components['schemas']['ProductFactRead']
type FactValue = Fact['value']

const SIDES = ['dimensions.height_in', 'dimensions.width_in', 'dimensions.depth_in'] as const
const WEIGHT = 'dimensions.weight_lbs'
const GTINS = [
  'identity.gtin_each',
  'identity.gtin_inner_pack',
  'identity.gtin_outer_pack',
] as const
const SIDE_LETTERS: Record<string, string> = {
  'dimensions.height_in': 'H',
  'dimensions.width_in': 'W',
  'dimensions.depth_in': 'D',
}

function numberText(value: FactValue) {
  return value == null || value === '' ? null : formatNumber(Number(value))
}

function boxText(values: FactValue[]) {
  const sides = values.map(numberText)
  return sides.every((side) => side != null) ? `${sides.join(' × ')} in` : null
}

function errorMessage(error: unknown) {
  const detail = (error as { detail?: unknown })?.detail
  return typeof detail === 'string' ? detail : 'Could not save the correction'
}

function shortDate(value: string | null | undefined) {
  return value ? parseUTCDate(value).toLocaleDateString() : null
}

function sourceWord(source: string | null | undefined) {
  return source === 'Placeholder' ? 'placeholder' : 'estimated'
}

/** Where a value came from, said once under it. */
function Provenance({ facts }: { facts: Fact[] }) {
  const corrected = facts.find((fact) => fact.correction)?.correction
  const explanation = facts.find((fact) => fact.explanation)?.explanation
  if (corrected) {
    return (
      <span className="block text-xs font-normal text-muted-foreground">
        Corrected{corrected.by ? ` by ${corrected.by}` : ''}
        {corrected.expires_at ? ` until ${shortDate(corrected.expires_at)}` : ''}
      </span>
    )
  }
  if (!explanation) return null
  return <span className="block text-xs font-normal text-muted-foreground">{explanation}</span>
}

/** Every source's value for these facts; the one in use is checked, invalid ones are marked. */
function SourceList({
  facts,
  format,
}: {
  facts: Fact[]
  format: (values: FactValue[]) => string | null
}) {
  const bySource = new Map<string, { values: FactValue[]; used: boolean; ignored: boolean }>()
  facts.forEach((fact, index) => {
    for (const item of fact.sources ?? []) {
      const entry = bySource.get(item.source) ?? {
        values: facts.map(() => null as FactValue),
        used: false,
        ignored: false,
      }
      entry.values[index] = item.value
      entry.used ||= item.used
      entry.ignored ||= item.ignored
      bySource.set(item.source, entry)
    }
  })
  if (bySource.size === 0)
    return <p className="text-sm text-muted-foreground">No source has this.</p>
  return (
    <ul className="divide-y rounded-md border bg-card text-sm">
      {[...bySource.entries()].map(([source, entry]) => (
        <li key={source} className="flex items-center gap-2 px-3 py-2">
          <span className="w-24 shrink-0 text-muted-foreground">{source}</span>
          <span
            className={
              entry.ignored
                ? 'min-w-0 flex-1 text-muted-foreground line-through'
                : 'min-w-0 flex-1 tabular-nums'
            }
          >
            {format(entry.values) ?? entry.values.map((value) => value ?? '–').join(' × ')}
          </span>
          {entry.used && <Check className="h-4 w-4 shrink-0 text-primary" aria-label="In use" />}
          {entry.ignored ? (
            <span className="shrink-0 text-xs text-muted-foreground">Not valid</span>
          ) : !entry.used ? (
            <span className="shrink-0 text-xs text-muted-foreground">Not used</span>
          ) : null}
        </li>
      ))}
    </ul>
  )
}

function FactHistory({
  productId,
  keys,
  formatOne,
  format,
}: {
  productId: string
  keys: readonly string[]
  formatOne: (value: FactValue) => string | null
  format: (values: FactValue[]) => string | null
}) {
  const histories = useProductFactHistories(productId, keys)
  if (histories.some((history) => history.isLoading)) return <Skeleton className="h-16" />
  // One line per change: the sides of a size change together, at the same moment.
  const moments = new Map<
    string,
    {
      at: string
      values: Map<string, FactValue>
      who: string | null
      source: DataSourceEmail | null
    }
  >()
  histories.forEach((history, index) => {
    for (const change of history.data ?? []) {
      const at = change.valid_from.slice(0, 19)
      const moment = moments.get(at) ?? {
        at: change.valid_from,
        values: new Map(),
        who: null,
        source: null,
      }
      moment.values.set(keys[index]!, change.value)
      moment.who ??= change.by ?? change.explanation ?? null
      // A value the data agent took from a manufacturer's email links to that email (USA-796).
      moment.source ??= change.source_email ?? null
      moments.set(at, moment)
    }
  })
  const changes = [...moments.values()].sort((a, b) => b.at.localeCompare(a.at))
  if (changes.length === 0) return null
  return (
    <ol className="space-y-1 text-xs text-muted-foreground">
      {changes.slice(0, 8).map((change) => {
        const text =
          change.values.size === keys.length
            ? format(keys.map((key) => change.values.get(key) ?? null))
            : [...change.values.entries()]
                .map(
                  ([key, value]) =>
                    `${keys.length > 1 ? `${SIDE_LETTERS[key] ?? ''} ` : ''}${formatOne(value) ?? 'removed'}`,
                )
                .join(', ')
        return (
          <li key={change.at} className="flex gap-2">
            <span className="w-20 shrink-0 tabular-nums">{shortDate(change.at)}</span>
            <span className="min-w-0 text-foreground tabular-nums">{text ?? 'Removed'}</span>
            <span className="min-w-0 truncate">{change.who}</span>
            <SourceEmailLink source={change.source} className="ml-auto shrink-0" />
          </li>
        )
      })}
    </ol>
  )
}

function CorrectionForm({
  productId,
  facts,
  inputs,
  close,
}: {
  productId: string
  facts: Fact[]
  inputs: Array<{ key: string; label: string; unit?: string; mono?: boolean }>
  close: () => void
}) {
  const correct = useCorrectProductFacts(productId)
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      inputs.map(({ key }) => {
        const value = facts.find((fact) => fact.key === key)?.value
        return [
          key,
          value == null ? '' : String(Number.isNaN(Number(value)) ? value : Number(value)),
        ]
      }),
    ),
  )
  const [reason, setReason] = useState('')
  const [expiresOn, setExpiresOn] = useState('')
  const corrected = facts.some((fact) => fact.correction)
  const keys = inputs.map(({ key }) => key)

  const save = () =>
    correct.mutate(
      {
        kind: 'correct',
        body: { values, reason: reason || null, expires_on: expiresOn || null },
      },
      {
        onSuccess: close,
        onError: (error) => toast.error(errorMessage(error)),
      },
    )
  const release = () =>
    correct.mutate(
      { kind: 'release', keys },
      {
        onSuccess: close,
        onError: (error) => toast.error(errorMessage(error)),
      },
    )

  return (
    <form
      className="space-y-3"
      onSubmit={(event) => {
        event.preventDefault()
        save()
      }}
    >
      <div className="grid grid-cols-3 gap-2">
        {inputs.map(({ key, label, unit, mono }) => (
          <div key={key} className={inputs.length === 1 ? 'col-span-3 sm:col-span-2' : undefined}>
            <Label htmlFor={`fact-${key}`} className="text-xs text-muted-foreground">
              {label}
              {unit ? ` (${unit})` : ''}
            </Label>
            {/* The cell's Edit control is the click-to-edit; these are the boxes it opens to. */}
            <InlineFieldInput
              id={`fact-${key}`}
              inputMode={mono ? 'numeric' : 'decimal'}
              className={mono ? 'font-mono' : 'tabular-nums'}
              value={values[key] ?? ''}
              onChange={(event) =>
                setValues((current) => ({
                  ...current,
                  [key]: event.target.value,
                }))
              }
            />
          </div>
        ))}
      </div>
      <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
        <div>
          <Label htmlFor={`fact-reason-${keys[0]}`} className="text-xs text-muted-foreground">
            Reason
          </Label>
          <InlineFieldInput
            id={`fact-reason-${keys[0]}`}
            placeholder="Optional"
            maxLength={500}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </div>
        <div>
          <Label htmlFor={`fact-expires-${keys[0]}`} className="text-xs text-muted-foreground">
            Expires
          </Label>
          <InlineFieldInput
            id={`fact-expires-${keys[0]}`}
            type="date"
            value={expiresOn}
            onChange={(event) => setExpiresOn(event.target.value)}
          />
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <LoadingButton
          type="submit"
          size="sm"
          loading={correct.isPending && correct.variables?.kind === 'correct'}
          loadingText="Saving..."
        >
          Save correction
        </LoadingButton>
        {corrected && (
          <LoadingButton
            type="button"
            size="sm"
            variant="outline"
            loading={correct.isPending && correct.variables?.kind === 'release'}
            loadingText="Removing..."
            onClick={release}
          >
            Remove correction
          </LoadingButton>
        )}
      </div>
    </form>
  )
}

function FactCell({
  productId,
  label,
  facts,
  editable,
  format,
  formatOne,
  inputs,
  className,
  shippingNote,
}: {
  productId: string
  label: string
  facts: Fact[]
  editable: boolean
  format: (values: FactValue[]) => string | null
  formatOne: (value: FactValue) => string | null
  inputs: Array<{ key: string; label: string; unit?: string; mono?: boolean }>
  className?: string
  /** What shipping uses instead when no source has this fact. */
  shippingNote?: string | null
}) {
  const value = format(facts.map((fact) => fact.value))
  const mono = inputs.every((input) => input.mono)
  return (
    <ExpandableSettingCell
      label={label}
      className={className}
      readOnly={!editable}
      summary={
        <>
          <span
            className={
              value
                ? mono
                  ? 'block font-mono text-xs'
                  : 'block tabular-nums'
                : 'block font-normal text-muted-foreground'
            }
          >
            {value ?? 'Not set'}
          </span>
          <Provenance facts={facts} />
          {!value && shippingNote ? (
            <span className="block text-xs font-normal text-muted-foreground">{shippingNote}</span>
          ) : null}
        </>
      }
    >
      {(close) => (
        <div className="space-y-4">
          <SourceList facts={facts} format={format} />
          {editable && (
            <CorrectionForm productId={productId} facts={facts} inputs={inputs} close={close} />
          )}
          <FactHistory
            productId={productId}
            keys={facts.map((fact) => fact.key)}
            formatOne={formatOne}
            format={format}
          />
        </div>
      )}
    </ExpandableSettingCell>
  )
}

/** Size, weight and barcodes: the value in use, its source, every source's value and staff corrections. */
export function ProductFactsSection({
  productId,
  afterWeight,
  afterGtins,
}: {
  productId: string
  /** Read-only cells that complete the grid (lead time, selling unit…). */
  afterWeight?: React.ReactNode
  afterGtins?: React.ReactNode
}) {
  const { data, isLoading, isError, refetch } = useProductFacts(productId)
  const { data: shipping } = useProductShippingFacts(productId)
  const byKey = new Map((data?.facts ?? []).map((fact) => [fact.key, fact]))
  const pick = (keys: readonly string[]) =>
    keys.map((key) => byKey.get(key)).filter((fact): fact is Fact => !!fact)
  const editable = data?.editable ?? false
  const gtinLabels: Record<string, string> = {
    'identity.gtin_each': 'GTIN',
    'identity.gtin_inner_pack': 'Inner pack GTIN',
    'identity.gtin_outer_pack': 'Case GTIN',
  }

  return (
    <section aria-labelledby="product-facts" className="overflow-hidden rounded-xl border bg-card">
      <h2 id="product-facts" className="border-b px-4 py-3 text-sm font-semibold">
        Product facts
      </h2>
      {isLoading ? (
        <div className="grid grid-cols-2 gap-px lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="m-3 h-10" />
          ))}
        </div>
      ) : isError ? (
        <div className="flex items-center gap-3 px-4 py-3 text-sm">
          <span className="text-destructive">Could not load the product facts.</span>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Try again
          </Button>
        </div>
      ) : (
        <div className="-mb-px -mr-px grid grid-cols-2 lg:grid-cols-4">
          <FactCell
            productId={productId}
            label="Size (H × W × D)"
            className="col-span-2"
            shippingNote={
              shipping?.size_estimated
                ? `Shipping uses ${sourceWord(shipping.size_source)} ${formatNumber(shipping.height_in)} × ${formatNumber(shipping.width_in)} × ${formatNumber(shipping.length_in)} in`
                : null
            }
            facts={pick(SIDES)}
            editable={editable}
            format={boxText}
            formatOne={(value) => (numberText(value) ? `${numberText(value)} in` : null)}
            inputs={[
              { key: SIDES[0], label: 'Height', unit: 'in' },
              { key: SIDES[1], label: 'Width', unit: 'in' },
              { key: SIDES[2], label: 'Depth', unit: 'in' },
            ]}
          />
          <FactCell
            productId={productId}
            label="Weight"
            shippingNote={
              shipping?.weight_estimated
                ? `Shipping uses ${sourceWord(shipping.weight_source)} ${formatNumber(shipping.weight_lbs)} lb`
                : null
            }
            facts={pick([WEIGHT])}
            editable={editable}
            format={([value]) =>
              numberText(value ?? null) ? `${numberText(value ?? null)} lb` : null
            }
            formatOne={(value) => (numberText(value) ? `${numberText(value)} lb` : null)}
            inputs={[{ key: WEIGHT, label: 'Weight', unit: 'lb' }]}
          />
          {afterWeight}
          {GTINS.map((key) => (
            <FactCell
              key={key}
              productId={productId}
              label={gtinLabels[key]!}
              facts={pick([key])}
              editable={editable}
              format={([value]) => (value ? String(value) : null)}
              formatOne={(value) => (value ? String(value) : null)}
              inputs={[{ key, label: gtinLabels[key]!, mono: true }]}
            />
          ))}
          {afterGtins}
        </div>
      )}
    </section>
  )
}
